import {useQuery} from '@apollo/client/react';

import {GET_MY_MEMBERSHIPS} from '../api/company';
import {useMyPermissions} from '../api/permissions';
import type {Capability, CompanyRole} from '../gqlApp/graphql';

/*
 * Группы кабинетных ролей (SKS-819) — копия apps/api/src/common/tenancy/company-role-groups.ts.
 *
 * Копия, а не общий пакет: на фронт они нужны только чтобы не показывать кнопку,
 * которую сервер всё равно отобьёт. Меняется группа там — меняется и здесь, иначе
 * кабинет начнёт предлагать действие, на котором человек получит «Недостаточно прав».
 */

/** Владелец и руководитель: всё «как владелец», кроме юридически значимого. */
export const OWNER_OR_HEAD: readonly CompanyRole[] = ['OWNER', 'HEAD'];

export const isOwnerOrHead = (role: CompanyRole | null | undefined): boolean =>
	Boolean(role && OWNER_OR_HEAD.includes(role));

/**
 * Сисадмин и служба безопасности ФФ. Членство в компании им само по себе ничего
 * не открывает: сервер пускает их только туда, где метод назвал их право. Разделы
 * без отдельного права (чаты, звонки, подключения, каталог «Сеть») для них закрыты,
 * и кабинет их не показывает.
 */
const SCOPED_ROLES: readonly CompanyRole[] = ['SYSADMIN', 'SECURITY'];

export const isScopedRole = (role: CompanyRole | null | undefined): boolean =>
	Boolean(role && SCOPED_ROLES.includes(role));

/**
 * Роль текущего пользователя в компании. undefined — ещё не известна.
 *
 * Из списка членств, а не из карточки компании: список один на весь кабинет
 * и отдаётся из кэша, а карточку пришлось бы запрашивать на каждой странице.
 */
export function useMyCompanyRole(companyId: string | undefined): {
	role: CompanyRole | null | undefined;
	error: Error | undefined;
} {
	const {data, error} = useQuery(GET_MY_MEMBERSHIPS, {skip: !companyId});

	if (!companyId || !data) {
		return {role: undefined, error};
	}

	return {
		role: data.myCompanies.find((company) => company.id === companyId)?.myRole ?? null,
		error,
	};
}

/**
 * Проверка права роли для кнопок действий.
 *
 * Строгая: пока права не загрузились, действия нет. Меню поступает наоборот
 * (показывает всё, чтобы не мигать), но кнопка «Принять», мелькнувшая у службы
 * безопасности, — это уже обещание, которое сервер не выполнит.
 */
export function useCan(companyId: string | undefined): (capability: Capability) => boolean {
	const {data} = useMyPermissions(companyId);
	const permissions = data?.myPermissions;

	return (capability) => Boolean(permissions?.includes(capability));
}
